// =============================================================================
// components/markets/WalletOnboardingSheet.tsx — the Cryb Wallet onboarding
// sheet (IMG_5881 structure): full-screen brand-gradient hero + white bottom
// sheet with a wallet glyph, one-line pitch, primary "Create My Wallet" and the
// secondary "I already have a wallet" link.
//
// v1 GATE: rendered but BLOCKED. Both CTAs are visible and gorgeous, and both
// resolve to the Coming-Soon alert ("Wallets land in CRYB v2") — no wallet is
// created, no key is imported, no money endpoint is called (App Store 3.1.1 /
// 3.1.5 safe). In v2 the two handlers swap for the real create/import flows.
// =============================================================================
import React from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../ui/Icon';
import ComingSoonBadge from '../ComingSoonBadge';
import { GlassButton } from '../glass';
import { colors, spacing, radius, fonts } from '../../constants/theme';

export interface WalletOnboardingSheetProps {
  visible: boolean;
  onClose: () => void;
}

// The v1 gate for both CTAs. Copy is the sprint-specified line.
function gate() {
  Alert.alert(
    'Coming Soon',
    'Wallets land in CRYB v2. You’re early — your spot is saved. 🚀',
    [{ text: 'Got it' }]
  );
}

export default function WalletOnboardingSheet({ visible, onClose }: WalletOnboardingSheetProps) {
  const insets = useSafeAreaInsets();
  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose} presentationStyle="fullScreen">
      {/* Gradient hero (IMG_5881) */}
      <LinearGradient
        colors={[colors.primary, colors.primaryLight]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.hero}
      >
        <TouchableOpacity
          style={[styles.closeBtn, { top: insets.top + spacing.md }]}
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Close"
        >
          <Ionicons name="close" size={20} color={colors.white} />
        </TouchableOpacity>
        {/* Soft hero blobs, echoing the old design's flowing shapes */}
        <View style={[styles.blob, styles.blobA]} />
        <View style={[styles.blob, styles.blobB]} />
      </LinearGradient>

      {/* Bottom sheet */}
      <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.xl }]}>
        <LinearGradient
          colors={colors.gradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.walletGlyph}
        >
          <Ionicons name="wallet" size={30} color={colors.white} />
        </LinearGradient>

        <Text style={styles.title}>Cryb Wallet</Text>
        <ComingSoonBadge label="Coming Soon · CRYB v2" />
        <Text style={styles.copy}>
          One wallet for your coins, NFTs and tips — built right into CRYB.
          Create it in seconds, or bring the wallet you already use.
        </Text>

        {/* v1: both CTAs render but resolve to the Coming-Soon gate. */}
        <GlassButton
          title="Create My Wallet"
          icon="sparkles-outline"
          variant="primary"
          size="lg"
          fullWidth
          onPress={gate}
        />
        <TouchableOpacity
          onPress={gate}
          hitSlop={{ top: 8, bottom: 8, left: 16, right: 16 }}
          accessibilityRole="button"
          accessibilityLabel="I already have a wallet"
        >
          <Text style={styles.linkText}>I already have a wallet</Text>
        </TouchableOpacity>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  hero: {
    flex: 1,
    overflow: 'hidden',
  },
  closeBtn: {
    position: 'absolute',
    right: spacing.lg,
    zIndex: 2,
    width: 36,
    height: 36,
    borderRadius: radius.full,
    backgroundColor: 'rgba(255,255,255,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  blob: {
    position: 'absolute',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 999,
  },
  blobA: { width: 260, height: 260, top: '18%', left: -80 },
  blobB: { width: 200, height: 320, bottom: -40, right: -60, borderRadius: 120 },
  sheet: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    marginTop: -24,
    paddingHorizontal: spacing.xxl,
    paddingTop: spacing.xxxl,
    alignItems: 'center',
    gap: spacing.md,
  },
  walletGlyph: {
    width: 64,
    height: 64,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: colors.primary,
    shadowOpacity: 0.3,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
  title: { ...fonts.extrabold, fontSize: 26, color: colors.ink },
  copy: {
    ...fonts.medium,
    fontSize: 14,
    lineHeight: 21,
    color: colors.ink2,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  linkText: {
    ...fonts.semibold,
    fontSize: 15,
    color: colors.primary,
    paddingVertical: spacing.sm,
  },
});
